/* eslint-disable max-lines-per-function */
import { useNavigate } from "react-router-dom";
import { find, get } from "lodash";
import React from "react";
import ReactQuill from "react-quill";
import { actionFooterStyles, BackButton, Button } from "components/haven-design";
import { Select } from "components/haven-design/form/select";
import { WaitlistUpdateQuery_interestList_scheduledUpdate_requested_results as RequestedResults } from "types/gql-types";
import { Channel } from "../../types";
import { Template } from "../types";
import { PreviewVariablesButton } from "./preview-variables-button";

const selectStyles = {
  width: 200,
  height: 32,
  lineHeight: "30px",
  padding: "0px 8px",
  fontSize: 13,
  marginRight: 16,
};
type Props = {
  requests: RequestedResults[];
  templates: Template[];
  setTemplate: (val: Template | null) => void;
  currentTemplate: Template | null;
  channel: Channel;
  isStepComplete: boolean;
  hasSkippedStep?: boolean;
  setSkipStep?: (hasSkipped: boolean) => void;
}

const filterByChannel = (channel: Channel) => (tmpl: Template) =>
  tmpl.type as unknown as Channel === channel;

const getDataByChannel = (channel: Channel) => {
  switch (channel) {
    case Channel.SMS:
      return {
        heading: "Review Text Message Template",
        desc: "Next, we’ll let applicants know through text messages.",
        backButtonUrl: "choose-email",
        forwardButtonUrl: "review",
      };

    case Channel.LETTER:
      return {
        heading: "Review Physical Letter Template",
        desc:
          "First let's choose a template for physical letters. No letters will be sent until later.",
        backButtonUrl: "removals",
        forwardButtonUrl: "choose-email",
      };

    case Channel.EMAIL:
      return {
        heading: "Review Email Template",
        desc: "Next, we’ll let applicants know through email.",
        backButtonUrl: "choose-letter",
        forwardButtonUrl: "choose-sms",
      };
    default:
      throw new Error("Unknown channel type");
  }
};

const ChooseTemplateStep: React.FC<Props> = props => {
  const navigate = useNavigate();
  const { channel, templates, currentTemplate: template, setTemplate } = props;
  const data = getDataByChannel(channel);
  const requestedInterestIds = props.requests.map(req => req.interestId);

  return (
    <div>
      <h2 className="margin-bottom-s">{data.heading}</h2>
      <p className="color-secondary margin-bottom-m">{data.desc}</p>

      <div style={{ display: "flex", flexDirection: "row" }}>
        {!props.hasSkippedStep && (
          <>
            <Select
              style={selectStyles}
              value={get(props, "currentTemplate.id", "")}
              onChange={({ target: { value } }) => {
                const tmpl = find(templates, t => t.id === value);
                setTemplate(tmpl || null);
              }}
            >
              <option value={""}>Select</option>
              {templates.filter(filterByChannel(channel)).map(tmpl => (
                <option value={tmpl.id} key={tmpl.id}>
                  {tmpl.name}
                </option>
              ))}
            </Select>

            <PreviewVariablesButton
              channel={channel}
              disabled={!props.isStepComplete}
              requestedInterestIds={requestedInterestIds}
              templateId={props.currentTemplate ? props.currentTemplate.id : null}
            />
          </>
        )}

        {channel === Channel.LETTER && (
          <a
            href="#"
            style={props.hasSkippedStep ? { margin: "0px 0px 32px 0" } : { margin: "7px 0 0 16px" }}
            onClick={e => {
              e.preventDefault();
              if (!props.setSkipStep) return;
              props.setSkipStep(!props.hasSkippedStep);
            }}
          >
            {props.hasSkippedStep ? "Use Letter" : "Skip Sending Letter"}
          </a>
        )}
      </div>

      {!props.hasSkippedStep && (
        <>
          <ReactQuill
            readOnly
            className="margin-bottom-l"
            modules={{ toolbar: false }}
            value={template ? JSON.parse(template.body) : ""}
          />
        </>
      )}

      <div className={actionFooterStyles.container}>
        <BackButton color="secondary" size="L" onClick={() => navigate(data.backButtonUrl)} />
        <div className={actionFooterStyles.spacer} />
        <Button
          size="L"
          color="primary"
          disabled={!props.isStepComplete}
          onClick={() => navigate(data.forwardButtonUrl)}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default ChooseTemplateStep;
